<template>
  <div>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <!-- Page Header -->
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center mb-2">
                    <v-btn
                      small
                      color="primary"
                      rounded
                      @click="$router.back()"
                      class="mr-4"
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <div>
                      <h2 class="text-h5 font-weight-bold primary--text mb-1">
                        {{ isDeletePage() != true ? 'Deposit Status Management' : 'Deleted Deposit Records' }}
                      </h2>
                      <p class="text-body-2 grey--text ma-0">
                        {{ isDeletePage() != true ? 'Review and manage user deposit requests' : 'Restore deleted deposit records' }}
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="success"
                      elevation="2"
                      rounded
                      @click="exportToExcel"
                      class="px-4"
                    >
                      <v-icon left small>mdi-microsoft-excel</v-icon>
                      Export Deposits
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-6"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-2 pt-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" color="primary">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Filter Deposits</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      small
                      rounded="pill"
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active Filter(s)
                    </v-chip>
                    <v-btn
                      text
                      small
                      color="error"
                      rounded
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-6 pb-6">
              <v-row class="align-center">
                <!-- Status Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Active Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Payment Status Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filter.status"
                    :items="paymentStatusItems"
                    label="Payment Status"
                    prepend-inner-icon="mdi-cash-check"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- User Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filter.user_id"
                    :items="userItems"
                    label="Select User"
                    prepend-inner-icon="mdi-account"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Deposit Transactions Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="lg">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="24">mdi-cash-plus</v-icon>
                    <span class="text-h6 font-weight-medium">Deposits List</span>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined class="mr-3">
                      <v-icon left small>mdi-cash-plus</v-icon>
                      {{ count }} {{ count === 1 ? "Transaction" : "Transactions" }} Found
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} -
                      {{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-3">
                      <span class="text-caption grey--text mr-2">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn small outlined :value="10">10</v-btn>
                        <v-btn small outlined :value="25">25</v-btn>
                        <v-btn small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="60"
                  ></v-progress-circular>
                  <p class="mt-3 grey--text">Loading deposits...</p>
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="transactions"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.is_active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- User Info Column -->
                <template #[`item.user_info`]="{ item }">
                  <div>
                    <div class="font-weight-medium mb-1">
                      {{ item.user?.first_name || "N/A" }}
                    </div>
                    <div class="text-caption grey--text">
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ item.user?.phone || "N/A" }}
                    </div>
                  </div>
                </template>

                <!-- Amount Column -->
                <template #[`item.amount`]="{ item }">
                  <div class="text-right">
                    <div class="font-weight-bold text-h6 success--text">
                      {{ formatCurrency(item.amount) }}
                    </div>
                  </div>
                </template>

                <!-- Payment Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="getPaymentStatusColor(item.status)"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ getPaymentStatusIcon(item.status) }}
                    </v-icon>
                    {{ getPaymentStatus(item.status) }}
                  </v-chip>
                </template>

                <!-- Created Date & Time Column -->
                <template #[`item.created_at`]="{ item }">
                  <div>
                    <div class="font-weight-medium mb-1">
                      {{ formatDate(item.created_at) }}
                    </div>
                    <div class="text-caption grey--text">
                      <v-icon x-small class="mr-1">mdi-clock</v-icon>
                      {{ formatTime(item.created_at) }}
                    </div>
                  </div>
                </template>

                <!-- Payment Proofs Column -->
                <template #[`item.payment_proofs`]="{ item }">
                  <div v-if="item.payment_mode == 2" class="d-flex flex-column gap-1">
                    <v-btn
                      v-if="item.upi_screenshot"
                      x-small
                      color="primary"
                      outlined
                      @click="viewImage(item.upi_screenshot, 'UPI Screenshot')"
                    >
                      <v-icon x-small class="mr-1">mdi-image</v-icon>
                      UPI
                    </v-btn>
                    <v-btn
                      v-if="item.bank_statement"
                      x-small
                      color="secondary"
                      outlined
                      @click="viewImage(item.bank_statement, 'Bank Statement')"
                    >
                      <v-icon x-small class="mr-1">mdi-file-document</v-icon>
                      Bank
                    </v-btn>
                    <span v-if="!item.upi_screenshot && !item.bank_statement" class="text-caption grey--text">
                      No proofs
                    </span>
                  </div>
                  <div v-else class="text-caption grey--text text-center">
                    Cash Payment
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center gap-2">
                    <template v-if="isDeletePage() != true">
                      <!-- Approve Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="success"
                            rounded
                            @click="updatePaymentStatus(item.id, 1)"
                            :disabled="item.status === 1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-check</v-icon>
                          </v-btn>
                        </template>
                        <span>Approve Transaction</span>
                      </v-tooltip>

                      <!-- Reject Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="error"
                            rounded
                            @click="updatePaymentStatus(item.id, 2)"
                            :disabled="item.status === 2"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </template>
                        <span>Reject Transaction</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">mdi-cash-plus</v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Deposits Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No deposit transactions match your current filters. Try adjusting your search criteria."
                          : "There are no deposit requests to review at this time."
                      }}
                    </p>
                    <v-btn
                      v-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 grey--text font-weight-medium">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} to
                      {{ Math.min(page * itemsPerPage, count) }} of
                      {{ count }} deposits
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-3"
                    >
                      <v-icon small left>mdi-chevron-left</v-icon>
                      Previous
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      circle
                      class="mx-2"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-3"
                    >
                      Next
                      <v-icon small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-body-2 grey--text mr-3 font-weight-medium">Go to page:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details="auto"
                      rounded
                      hide-spin-buttons
                      class="no-spin-arrows"
                      style="max-width: 90px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <!-- Clear Success Dialog Box -->
      <v-dialog v-model="is_deleted_transaction" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Transaction deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_transaction = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_transaction" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Transaction restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_transaction = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Image Viewer Dialog -->
      <v-dialog v-model="imageDialog" max-width="800">
        <v-card>
          <v-card-title class="text-h6">
            {{ imageTitle }}
            <v-spacer></v-spacer>
            <v-btn icon @click="imageDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="text-center pa-4">
            <img
              v-if="selectedImage"
              :src="getImageUrl(selectedImage)"
              :alt="imageTitle"
              style="max-width: 100%; max-height: 500px; object-fit: contain;"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "depositStatusPage",
  data() {
    return {
      filter: {
        is_active: "",
        user_id: "",
        show_deleted: false,
        status: "",
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      jumpToPage: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      transactions: [],
      statuses: [
        {
          text: "All Status",
          value: "",
        },
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      paymentStatusItems: [
        {
          text: "All Payment Status",
          value: "",
        },
        { text: "Pending", value: 0 },
        { text: "Approved", value: 1 },
        { text: "Rejected", value: 2 },
      ],
      userItems: [
        {
          text: "All Users",
          value: "",
        },
      ],
      isLoading: false,
      delete_transaction: false,
      is_deleted_transaction: false,
      is_restored_transaction: false,
      imageDialog: false,
      selectedImage: null,
      imageTitle: '',
      headers: [
        { text: "Sr No.", value: "sr_no", sortable: false, width: "70px" },
        { text: "Status", value: "is_active", width: "120px" },
        {
          text: "User Information",
          value: "user_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Amount",
          value: "amount",
          sortable: false,
          width: "150px",
          align: "right",
        },
        {
          text: "Payment Status",
          value: "status",
          sortable: false,
          width: "150px",
        },
        {
          text: "Created Date & Time",
          value: "created_at",
          sortable: false,
          width: "180px",
        },
        {
          text: "Payment Proofs",
          value: "payment_proofs",
          sortable: false,
          width: "150px",
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "150px",
          align: "center",
        },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filter.is_active ||
        this.filter.user_id ||
        this.filter.status !== ""
      );
    },
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=transaction`)
        .then((masters) => {
          masters = masters.data;
          //Position
          masters.users.forEach((user) => {
            this.userItems.push({
              id: user.id,
              text: user.first_name,
              value: user.id,
            });
          });
        })
        .catch(function (error) {
          console.log(error);
          // redirect to Home Page
          // window.location.href = "/auth/login";
        });
    },
    async getData() {
      console.log("Getting deposit data...");
      this.isLoading = true;

      // Set filters with default values
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;

      const filters = {
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        transaction_type: 2,
        transaction_purpose: 1,
        is_active: this.filter.is_active || "",
        user_id: this.filter.user_id || "",
        status: this.filter.status !== "" ? this.filter.status : "",
        show_deleted: this.filter.show_deleted,
      };

      let query = new URLSearchParams(filters);

      try {
        const response = await axios.get(`user_transactions?${query}`);
        this.transactions = response.data.data || [];
        this.count = response.data.count || 0;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.error("Failed to fetch deposit transactions:", error);
        this.transactions = [];
        this.count = 0;
      }

      this.isLoading = false;
    },

    clearAllFilters() {
      this.filter = {
        is_active: "",
        user_id: "",
        show_deleted: this.filter.show_deleted,
        status: "",
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.is_active) count++;
      if (this.filter.user_id) count++;
      if (this.filter.status !== "") count++;
      return count;
    },

    isDeletePage() {
      return this.$route.query.open_trash;
    },

    changeItemsPerPageToggle(value) {
      if (value !== null && value !== undefined) {
        this.itemsPerPage = value;
        this.selectedItemsPerPage = value;
        this.page = 1;
        this.getData();
      }
    },

    goToPage() {
      const pageNum = parseInt(this.jumpToPage);
      if (pageNum >= 1 && pageNum <= this.paginationLength) {
        this.page = pageNum;
      } else {
        this.jumpToPage = this.page;
      }
    },

    exportToExcel() {
      console.log("Export to Excel clicked");
      // Implement Excel export logic
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
      }).format(parseFloat(amount) || 0);
    },

    getPaymentStatusColor(status) {
      switch (status) {
        case 0: return "warning";
        case 1: return "success";
        case 2: return "error";
        default: return "grey";
      }
    },

    getPaymentStatusIcon(status) {
      switch (status) {
        case 0: return "mdi-clock";
        case 1: return "mdi-check-circle";
        case 2: return "mdi-close-circle";
        default: return "mdi-help-circle";
      }
    },
    async deleteData(id) {
      this.delete_transaction = true;
      await axios
        .post(`/transactions/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_transaction = false;
          this.is_deleted_transaction = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    async restoreData(id) {
      this.restore_transaction = true;
      await axios
        .post(`/transactions/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_transaction = false;
          this.is_restored_transaction = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          // window.location.href = "/auth/login";
        });
    },
    async updatePaymentStatus(id, status) {
      await axios
        .patch(`/user_transactions/${this.encryptIt(id)}`, { status })
        .then(() => {
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          // window.location.href = "/auth/login";
        });
    },
    getPaymentStatus(data) {
      switch (data) {
        case 0:
          return "Pending";

        case 1:
          return "Approved";

        case 2:
          return "Rejected";

        default:
          return "";
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },

    formatTime(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    viewImage(imagePath, title) {
      this.selectedImage = imagePath;
      this.imageTitle = title;
      this.imageDialog = true;
    },

    getImageUrl(imagePath) {
      if (!imagePath) return '';
      const mediaUrl = 'https://blr1.vultrobjects.com/space-1/';
      return `${mediaUrl}${imagePath}`;
    },
  },
};
</script>

<style scoped>
/* Enhanced Table Styles */
.enhanced-table >>> tbody tr {
  transition: all 0.3s ease;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.enhanced-table >>> tbody tr:nth-child(odd) {
  background-color: #fafbfc;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 16px 12px !important;
  font-size: 0.875rem !important;
}

.enhanced-table >>> tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f3f4 !important;
}

/* Card Enhancements */
.v-card {
  border-radius: 12px !important;
  border: 1px solid #f1f3f4;
}

.v-card--outlined {
  border: 2px solid #e3f2fd !important;
}

/* Chip Enhancements */
.v-chip {
  font-weight: 500;
  border-radius: 8px !important;
}

.v-chip--pill {
  border-radius: 50px !important;
}

/* Button Enhancements */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-btn--rounded {
  border-radius: 8px !important;
}

/* Input Field Enhancements */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

/* Pagination Enhancements */
.v-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  margin: 0 2px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.v-pagination >>> .v-pagination__item:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-1px);
}

.v-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

/* Hide number input spin buttons */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
  text-align: center;
}

/* Loading Animation */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.v-progress-circular {
  animation: pulse 2s infinite;
}

/* Spacing Utilities */
.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .enhanced-table >>> tbody td {
    padding: 12px 8px !important;
  }

  .v-card-title {
    padding: 16px !important;
  }

  .v-card-text {
    padding: 16px !important;
  }
}
</style>